import { Observable } from 'rxjs';

import { EPSG, Project, ProjectData, ResultPage } from '@mirage-projects/cw-models';

import { ProjectFilter } from '../models/filters/project-filter';

/**
 * Remote request parameters to get the list of projects
 */
export interface GetProjectsParams {
    author: string;
    page: number;
    ordering: string;
    page_size: number;
    onlyMyProjects: boolean;
    projectType: number;
    search: string;
    exclude_job_site: number;
}

/**
 * Abstract service definition to manage the projects endpoints
 */
export abstract class ProjectService {
    /**
     * Create a new project
     * @param projectData Creation parameters
     */
    abstract create(projectData: ProjectData): Observable<Project>;

    /**
     * Get project by id
     * @param id Project id
     */
    abstract get(id: number): Observable<Project>;

    /**
     * Get the list of projects
     * @param params Parameters to filter the list
     */
    abstract getProjects(params: any): Observable<ResultPage<Project>>;

    /**
     * Get the list of projects
     * @param projectFilter Parameters to filter the list
     */
    abstract getProjectsPage(projectFilter: ProjectFilter): Observable<ResultPage<Project>>;

    /**
     * Update a project
     * @param projectId The project id to update
     * @param projectData The data to update
     */
    abstract update(projectId: number, projectData: any): Observable<Project>;

    /**
     * Delete a project
     * @param project The project to delete
     * @param deleteOrphanLayers Set if the orphan layers should be deleted
     */
    abstract delete(project: Project, deleteOrphanLayers?: boolean): Observable<any>;

    /**
     * Add a layer to a project
     * @param projectId Id of the project
     * @param layerId  Id of the layer
     */
    abstract addLayer(projectId: number, layerId: number): Observable<any>;

    /**
     * Add a layer to a project
     * @param projectId Id of the project
     * @param layerIds  Id of the layer
     */
    abstract addLayers(projectId: number, layerId: number[]): Observable<any>;

    /**
     * Remove a layer from a project
     * @param projectId Id of the project
     * @param layerId Id of the layer
     */
    abstract removeLayer(projectId: number, layerId: number): Observable<any>;

    /**
     * Search EPSG
     * @param searchValue Search value
     * @param page Page number
     */
    abstract searchEPSG(searchValue: string, page?: number): Observable<ResultPage<EPSG>>;

    /**
     * Checks if a project has restricted sharing
     * @param projectId Project identification number
     */
    abstract isRestrictedSharedProject(projectId: number): Observable<boolean>;

    /**
     * Unshare restricted project
     * @param projectId Project identification number
     */
    abstract unshareRestrictedProject(projectId: number): Observable<void>;

    /**
     * Get the restricted user code for the get shared project link
     */
    abstract getRestrictedUserCode(projectId: number, queryParam: string[], params: Record<string, unknown>): Observable<string>;

    /**
     * Get the anonymous  user code for the get shared project link
     */
    abstract getAnonymousUserCode(): Observable<string>;
}
